<script setup>
import ParagraphText from '@/Common/Text/ParagraphText.vue'
import { secondaryDividerBorder } from '@/Common/util/tailwind/colorConstants'
import { computed, ref } from 'vue'
import { Shimmer } from '../Shimmer'
import ActivityDisclosure from './ActivityDisclosure.vue'
import ActivityList from './ActivityList.vue'
import { latestActivityLabel } from './activityLabels'

const props = defineProps({
  parts: { type: Array, default: () => [] },
  status: { type: String, default: 'complete' },
  durationMs: { type: Number, default: null },
  hasResponse: Boolean,
})

// Only an explicit click changes this. Streaming updates never undo the user's choice.
const open = ref(false)
const hasOpened = ref(false)
function toggleOpen() {
  hasOpened.value = true
  open.value = !open.value
}
const running = computed(() => props.status === 'running')
const latestSummary = computed(() =>
  latestActivityLabel(props.parts, props.status)
)
const label = computed(() => {
  const duration =
    typeof props.durationMs === 'number' && Number.isFinite(props.durationMs)
      ? `${Math.max(0, Math.round(props.durationMs / 1000))}s`
      : null
  if (running.value) return duration ? `Working for ${duration}` : 'Working…'
  if (props.status === 'waiting')
    return duration
      ? `Waiting for you · worked for ${duration}`
      : 'Waiting for you'
  if (props.status === 'error')
    return duration ? `Work failed after ${duration}` : 'Work failed'
  if (props.status === 'stopped')
    return duration ? `Stopped after ${duration}` : 'Work stopped'
  return duration ? `Worked for ${duration}` : 'View activity'
})
</script>

<template>
  <div class="min-w-0 w-full" data-testid="agent-activity">
    <ActivityDisclosure
      :open="open"
      :running="running"
      :label="label"
      :error="status === 'error'"
      data-testid="activity-disclosure"
      @toggle="toggleOpen"
    />
    <div
      v-if="hasOpened"
      v-show="open"
      class="mt-2 border-t pt-3"
      :class="secondaryDividerBorder"
      data-testid="activity-content"
    >
      <ActivityList :parts="parts" :status="status" />
    </div>
    <ParagraphText
      v-if="running && !hasResponse && latestSummary"
      class="mt-2 min-w-0"
      color="text-gray-500"
      data-testid="activity-latest-summary"
    >
      <Shimmer
        as="span"
        class="max-w-full truncate text-left"
        :title="latestSummary"
        data-testid="activity-shimmer"
        >{{ latestSummary }}</Shimmer
      >
    </ParagraphText>
  </div>
</template>
